/**
 * Portal Header Component
 *
 * Simplified header with mobile menu toggle and a user chip that links to the
 * Profile page (always visible top-right, unlike the below-the-fold sidebar
 * link). Shows the org switcher for super admins.
 *
 * The user chip reads the cached user via useStoredUser() so it updates
 * immediately when the display name is edited on the Profile page.
 */

import { NavLink } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { useStoredUser } from '@/services/auth/stored-user';
import { Button } from '@/components/ui/button';
import { Menu, User } from 'lucide-react';
import { OrgSwitcher } from './OrgSwitcher';

interface PortalHeaderProps {
  onMenuClick?: () => void;
}

export function PortalHeader({ onMenuClick }: PortalHeaderProps) {
  const { isSuperAdmin } = useAuth();
  const user = useStoredUser();

  const displayName =
    user?.displayName ||
    (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null) ||
    user?.firstName ||
    user?.emailAddress ||
    'User';
  const roleLabel = isSuperAdmin ? 'Super Admin' : user?.organizationRole || 'Member';

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
      {/* Left section */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onMenuClick}
          className="lg:hidden"
        >
          <Menu className="h-4 w-4" />
        </Button>
      </div>

      {/* Right section */}
      <div className="flex items-center space-x-4">
        {isSuperAdmin && <OrgSwitcher compact />}

        <NavLink
          to="/portal/profile"
          title="Your profile"
          className="flex items-center space-x-3 rounded-full py-1 pl-3 pr-1 transition-colors hover:bg-accent/50"
        >
          <div className="hidden sm:block text-right">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground capitalize">{roleLabel}</p>
          </div>
          <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground">
            {user?.imageUrl ? (
              <img src={user.imageUrl} alt={`${displayName} avatar`} className="h-full w-full object-cover" />
            ) : (
              <User className="h-5 w-5" />
            )}
          </div>
        </NavLink>
      </div>
    </header>
  );
}
